import { useEffect, useRef, useState } from "react";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Plus, GripVertical, Eye, EyeOff, Paintbrush, X, Image as ImageIcon, Trash2, BookmarkPlus } from "lucide-react";

export const DEFAULT_SECTIONS = {
  header: [],
  side: ["photo", "personal", "skills", "languages", "hobbies"],
  main: ["profile", "experience", "education", "extra", "references"],
};

export const SECTION_LABELS = {
  photo: "rbPhoto",
  personal: "rbSecPersonal", skills: "rbSecSkills", languages: "rbDndLanguages",
  hobbies: "rbSecHobbies", profile: "rbSecProfile", experience: "rbSecExperience",
  education: "rbSecEducation", extra: "rbSecExtra", references: "rbSecReferences",
};

export const PRESET_DESIGNS = {
  classic: { layout: "sidebar-left", accent: "#1A1A1A", font: "helvetica", header_center: false, side_bg: false, density: "normal" },
  modern: { layout: "sidebar-left", accent: "#14555C", font: "helvetica", header_center: false, side_bg: true, density: "normal" },
  elegant: { layout: "sidebar-left", accent: "#8A7440", font: "times", header_center: true, side_bg: false, density: "normal" },
};
export const PRESET_KEYS = ["layout", "accent", "font", "header_center", "side_bg", "density"];

export const PRESET_TEMPLATES = [
  { key: "classic", labelKey: "rbTplClassic", hintKey: "rbTplClassicHint", color: "#1A1A1A" },
  { key: "modern", labelKey: "rbTplModern", hintKey: "rbTplModernHint", color: "#14555C" },
  { key: "elegant", labelKey: "rbTplElegant", hintKey: "rbTplElegantHint", color: "#8A7440" },
];

const ACCENTS = ["#1A1A1A", "#14555C", "#8A7440", "#7A1F2B", "#1F3A5F", "#2F5233"];

export const newDesign = () => ({
  ...PRESET_DESIGNS.classic,
  photo_shape: "square",
  show_photo: true,
  hidden: [],
  section_styles: {},
  skill_bullets: "none",
  rate_skills: true,
  rate_languages: true,
  sections: {
    header: [...DEFAULT_SECTIONS.header],
    side: [...DEFAULT_SECTIONS.side],
    main: [...DEFAULT_SECTIONS.main],
  },
});

function LayoutIcon({ kind }) {
  const bar = "bg-neutral-400";
  return (
    <span className="w-10 h-7 border border-neutral-400 bg-white flex gap-[2px] p-[3px] shrink-0">
      {kind === "sidebar-left" && (<><span className={`w-1/3 ${bar}`} /><span className="flex-1 bg-neutral-200" /></>)}
      {kind === "sidebar-right" && (<><span className="flex-1 bg-neutral-200" /><span className={`w-1/3 ${bar}`} /></>)}
      {kind === "single-column" && <span className="flex-1 bg-neutral-200" />}
    </span>
  );
}

function ColorField({ label, value, onChange, testId }) {
  const { t } = useT();
  return (
    <div>
      <p className="ed-label mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <label
          className={`w-8 h-8 border cursor-pointer flex items-center justify-center ${value ? "border-neutral-900" : "border-dashed border-neutral-400"}`}
          style={{ background: value || "#FFFFFF" }}
          title={label}
        >
          <input
            type="color"
            data-testid={testId}
            className="opacity-0 absolute w-0 h-0"
            value={value || "#1A1A1A"}
            onChange={(e) => onChange(e.target.value)}
          />
          {!value && <Plus size={12} strokeWidth={1.5} className="text-neutral-400" />}
        </label>
        {value && (
          <button
            data-testid={`${testId}-clear`}
            onClick={() => onChange("")}
            className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
          >
            {t("tplSecNone")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TemplateDesigner({ design, onChange }) {
  const { t } = useT();
  const [dragKey, setDragKey] = useState(null);
  const [styleKey, setStyleKey] = useState(null);
  const [bgPresets, setBgPresets] = useState([]);
  const [myBgPresets, setMyBgPresets] = useState([]);
  const [savingBg, setSavingBg] = useState(false);
  const bgInputRef = useRef(null);
  const set = (patch) => onChange({ ...design, ...patch });
  const sections = design.sections || DEFAULT_SECTIONS;
  const hidden = design.hidden || [];
  const bg = design.background || null;
  const bgOpacityPct = Math.round(((bg?.opacity ?? 0.15)) * 100);

  const loadMyPresets = async () => {
    try {
      const { data } = await api.get("/cv-templates/my-bg-presets");
      setMyBgPresets(Array.isArray(data) ? data : []);
    } catch { /* silent — feature is optional */ }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/cv-templates/bg-presets")
      .then(({ data }) => { if (!cancelled) setBgPresets(Array.isArray(data) ? data : []); })
      .catch(() => { /* presets are optional; keep the panel usable */ });
    loadMyPresets();
    return () => { cancelled = true; };
  }, []);

  const applyPreset = (preset) => {
    set({ background: { image: preset.image, opacity: bg?.opacity ?? 0.15 } });
  };

  const isCurrentBgSaved = () => {
    if (!bg?.image) return true;
    const inBuiltIn = bgPresets.some((p) => p.image === bg.image);
    const inMine = myBgPresets.some((p) => p.image === bg.image);
    return inBuiltIn || inMine;
  };

  const saveCurrentBgAsPreset = async () => {
    if (!bg?.image || savingBg) return;
    const name = window.prompt(t("rbTplBgSavePrompt"), t("rbTplBgSaveDefaultName"));
    if (!name || !name.trim()) return;
    setSavingBg(true);
    try {
      await api.post("/cv-templates/my-bg-presets", { name: name.trim(), image: bg.image });
      await loadMyPresets();
      toast.success(t("rbTplBgSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rbTplBgSaveFailed"));
    } finally {
      setSavingBg(false);
    }
  };

  const deleteMyPreset = async (pid) => {
    try {
      await api.delete(`/cv-templates/my-bg-presets/${pid}`);
      setMyBgPresets((prev) => prev.filter((p) => p.id !== pid));
    } catch { /* silent */ }
  };

  const handleBackgroundFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 4 * 1024 * 1024) {
      // 4MB soft cap — mirrors the ~5MB CV upload cap and keeps design docs light
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      set({ background: { image: String(reader.result || ""), opacity: bg?.opacity ?? 0.15 } });
    };
    reader.readAsDataURL(file);
  };

  const clearBackground = () => set({ background: null });
  const setBgOpacity = (pct) => {
    if (!bg) return;
    set({ background: { ...bg, opacity: Math.max(0, Math.min(1, pct / 100)) } });
  };

  const zoneItems = (zone) => {
    let items = sections[zone] || [];
    // Legacy designs: photo was not a section yet — show it in the sidebar
    if (zone === "side" && !(sections.header || []).includes("photo") && !(sections.side || []).includes("photo") && !(sections.main || []).includes("photo")) {
      items = ["photo", ...items];
    }
    return items;
  };

  const moveSection = (key, zone, index) => {
    if (!key) return;
    const cur = {
      header: zoneItems("header").filter((k) => k !== key),
      side: zoneItems("side").filter((k) => k !== key),
      main: zoneItems("main").filter((k) => k !== key),
    };
    const list = cur[zone];
    if (index == null || index > list.length) list.push(key);
    else list.splice(index, 0, key);
    set({ sections: cur });
  };

  const toggleHidden = (key) => {
    const isHidden = hidden.includes(key);
    const next = isHidden ? hidden.filter((k) => k !== key) : [...hidden, key];
    const patch = { hidden: next };
    if (key === "photo") patch.show_photo = isHidden;
    set(patch);
  };

  const secStyle = (key) => (design.section_styles || {})[key] || {};
  const setSecStyle = (key, patch) => {
    const cur = { ...secStyle(key), ...patch };
    Object.keys(cur).forEach((k) => {
      if (cur[k] === "" || cur[k] === false || cur[k] == null) delete cur[k];
    });
    const all = { ...(design.section_styles || {}) };
    if (Object.keys(cur).length) all[key] = cur;
    else delete all[key];
    set({ section_styles: all });
  };

  const zoneLabel = (zone) => {
    if (zone === "header") return t("rbTplZoneHeader");
    if (zone === "side") return design.layout === "single-column" ? t("rbTplZoneTop") : t("rbTplZoneSide");
    return design.layout === "single-column" ? t("rbTplZoneBottom") : t("rbTplZoneMain");
  };

  const renderZone = (zone, minH) => (
    <div
      key={zone}
      data-testid={`tpl-dnd-${zone}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); moveSection(dragKey, zone, null); setDragKey(null); }}
      className={`border border-dashed border-neutral-400 bg-white/50 p-3 ${minH}`}
    >
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">{zoneLabel(zone)}</p>
      <div className={zone === "header" ? "flex flex-wrap gap-1.5" : "space-y-1.5"}>
        {zoneItems(zone).map((key, idx) => {
          const isHidden = hidden.includes(key);
          const hasStyle = Object.keys(secStyle(key)).length > 0;
          return (
            <div
              key={key}
              data-testid={`tpl-dnd-item-${key}`}
              draggable
              onDragStart={() => setDragKey(key)}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); moveSection(dragKey, zone, idx); setDragKey(null); }}
              className={`flex items-center gap-2 border bg-white pl-3 pr-1.5 py-1 cursor-grab active:cursor-grabbing font-mono text-[0.65rem] uppercase tracking-[0.15em] ${dragKey === key ? "opacity-40 border-neutral-900" : "border-neutral-300 hover:border-neutral-900"} ${isHidden ? "opacity-50" : ""}`}
            >
              <GripVertical size={12} strokeWidth={1.5} className="text-neutral-400 shrink-0" />
              <span className={isHidden ? "line-through" : ""}>{t(SECTION_LABELS[key])}</span>
              <span className="flex items-center gap-0.5 ml-auto">
                <button
                  data-testid={`tpl-style-${key}`}
                  onClick={(e) => { e.stopPropagation(); setStyleKey(styleKey === key ? null : key); }}
                  title={t("tplSecStyle")}
                  className={`p-1 transition-colors ${styleKey === key || hasStyle ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-900"}`}
                >
                  <Paintbrush size={12} strokeWidth={1.7} />
                </button>
                <button
                  data-testid={`tpl-hide-${key}`}
                  onClick={(e) => { e.stopPropagation(); toggleHidden(key); }}
                  title={isHidden ? t("tplSecShow") : t("tplSecHide")}
                  className={`p-1 transition-colors ${isHidden ? "text-rose-700" : "text-neutral-400 hover:text-neutral-900"}`}
                >
                  {isHidden ? <EyeOff size={12} strokeWidth={1.7} /> : <Eye size={12} strokeWidth={1.7} />}
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div data-testid="template-designer">
      {/* Presets */}
      <p className="ed-label mb-2">{t("rbTplPresets")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {PRESET_TEMPLATES.map(({ key, labelKey, hintKey, color }) => {
          const active = PRESET_KEYS.every((k) => design[k] === PRESET_DESIGNS[key][k]);
          return (
            <button
              key={key}
              data-testid={`tpl-preset-${key}`}
              onClick={() => set({ ...PRESET_DESIGNS[key] })}
              className={`ed-card p-4 text-left flex items-start gap-3 transition-colors ${active ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
            >
              <span className="mt-0.5 w-6 h-6 shrink-0 border border-neutral-300" style={{ background: color }} />
              <span className="min-w-0">
                <span className="font-serif text-lg block leading-tight">{t(labelKey)}</span>
                <span className="text-[0.65rem] font-mono text-neutral-500 block mt-0.5">{t(hintKey)}</span>
              </span>
              {active && <Check size={15} strokeWidth={2} className="ml-auto shrink-0" />}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
        {/* Layout */}
        <div>
          <p className="ed-label mb-2">{t("rbTplLayout")}</p>
          <div className="flex flex-col gap-2">
            {[
              ["sidebar-left", "rbTplLayoutLeft"],
              ["sidebar-right", "rbTplLayoutRight"],
              ["single-column", "rbTplLayoutSingle"],
            ].map(([key, labelKey]) => (
              <button
                key={key}
                data-testid={`tpl-layout-${key}`}
                onClick={() => set({ layout: key })}
                className={`flex items-center gap-3 border px-3 py-2 text-left transition-colors ${design.layout === key ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
              >
                <LayoutIcon kind={key} />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em]">{t(labelKey)}</span>
                {design.layout === key && <Check size={13} strokeWidth={2} className="ml-auto" />}
              </button>
            ))}
          </div>
        </div>

        {/* Accent + font */}
        <div className="space-y-6">
          <div>
            <p className="ed-label mb-2">{t("rbTplAccent")}</p>
            <div className="flex flex-wrap items-center gap-2">
              {ACCENTS.map((c) => (
                <button
                  key={c}
                  data-testid={`tpl-accent-${c.slice(1)}`}
                  onClick={() => set({ accent: c })}
                  title={c}
                  className={`w-8 h-8 border-2 transition-transform hover:scale-110 ${design.accent === c ? "border-neutral-900 ring-2 ring-offset-2 ring-neutral-900" : "border-white"}`}
                  style={{ background: c }}
                />
              ))}
              <label className="w-8 h-8 border border-dashed border-neutral-400 flex items-center justify-center cursor-pointer hover:border-neutral-900" title={t("rbTplAccentCustom")}>
                <input
                  type="color"
                  data-testid="tpl-accent-custom"
                  className="opacity-0 absolute w-0 h-0"
                  value={design.accent}
                  onChange={(e) => set({ accent: e.target.value })}
                />
                <Plus size={13} strokeWidth={1.5} />
              </label>
            </div>
          </div>
          <div>
            <p className="ed-label mb-2">{t("rbTplFont")}</p>
            <div className="flex flex-wrap gap-2">
              {[["helvetica", "rbTplFontModern"], ["times", "rbTplFontClassic"], ["courier", "rbTplFontMono"]].map(([key, labelKey]) => (
                <button
                  key={key}
                  data-testid={`tpl-font-${key}`}
                  onClick={() => set({ font: key })}
                  className={`px-4 py-2 border font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${design.font === key ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
                  style={{ fontFamily: key === "times" ? "Georgia, serif" : key === "courier" ? "monospace" : undefined }}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Density */}
        <div>
          <p className="ed-label mb-2">{t("rbTplDensity")}</p>
          <div className="flex gap-2">
            {[["compact", "rbTplDenseCompact"], ["normal", "rbTplDenseNormal"], ["airy", "rbTplDenseAiry"]].map(([key, labelKey]) => (
              <button
                key={key}
                data-testid={`tpl-density-${key}`}
                onClick={() => set({ density: key })}
                className={`px-4 py-2 border font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${design.density === key ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div>
          <p className="ed-label mb-2">{t("rbTplOptions")}</p>
          <div className="flex flex-col gap-2">
            {[["side_bg", "rbTplSideBg"], ["header_center", "rbTplHeaderCenter"], ["rate_skills", "rbTplRateSkills"], ["rate_languages", "rbTplRateLanguages"]].map(([key, labelKey]) => {
              const checked = key === "rate_skills" || key === "rate_languages"
                ? (design[key] !== false)
                : !!design[key];
              return (
                <button
                  key={key}
                  data-testid={`tpl-opt-${key}`}
                  onClick={() => set({ [key]: !checked })}
                  disabled={key === "side_bg" && design.layout === "single-column"}
                  className={`flex items-center gap-2 border px-3 py-2 text-left font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors disabled:opacity-40 ${checked ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
                >
                  <span className={`w-3.5 h-3.5 border flex items-center justify-center ${checked ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
                    {checked && <Check size={10} strokeWidth={3} />}
                  </span>
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Skill bullet style */}
      <div className="mt-6">
        <p className="ed-label mb-2">{t("rbTplSkillBullets")}</p>
        <div className="flex flex-wrap gap-2">
          {[
            ["none", "rbTplSkillBulletNone", ""],
            ["dot", "rbTplSkillBulletDot", "●"],
            ["square", "rbTplSkillBulletSquare", "▪"],
            ["dash", "rbTplSkillBulletDash", "–"],
            ["arrow", "rbTplSkillBulletArrow", "›"],
          ].map(([key, labelKey, glyph]) => {
            const active = (design.skill_bullets || "none") === key;
            return (
              <button
                key={key}
                data-testid={`tpl-skill-bullet-${key}`}
                onClick={() => set({ skill_bullets: key })}
                className={`px-3 py-2 border font-mono text-[0.65rem] uppercase tracking-[0.12em] transition-colors inline-flex items-center gap-2 ${active ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
              >
                {glyph && <span className="text-base leading-none">{glyph}</span>}
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Photo shape */}
      <div className="mt-6">
        <p className="ed-label mb-2">{t("rbTplPhoto")}</p>
        <div className="flex flex-wrap items-center gap-2">
          {[
            ["square", "rounded-none"], ["rounded", "rounded-md"], ["circle", "rounded-full"],
          ].map(([shape, cls]) => (
            <button
              key={shape}
              data-testid={`tpl-photoshape-${shape}`}
              onClick={() => set({ photo_shape: shape })}
              title={t(`rbTplShape_${shape}`)}
              className={`flex items-center gap-2 border px-3 py-2 transition-colors ${design.photo_shape === shape ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
            >
              <span className={`w-5 h-5 bg-neutral-500 ${cls}`} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t(`rbTplShape_${shape}`)}</span>
            </button>
          ))}
        </div>
        <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("rbTplPhotoHint")}</p>
      </div>

      {/* Background image (full A4, user-set opacity) */}
      <div className="mt-6" data-testid="tpl-bg-panel">
        <p className="ed-label mb-1 flex items-center gap-2">
          <ImageIcon size={12} strokeWidth={1.7} /> {t("rbTplBgTitle")}
        </p>
        <p className="text-[0.65rem] font-mono text-neutral-500 mb-3">{t("rbTplBgHint")}</p>
        <input
          ref={bgInputRef}
          data-testid="tpl-bg-file"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => { handleBackgroundFile(e.target.files?.[0]); e.target.value = ""; }}
        />
        {/* User's personal backgrounds — saved presets */}
        {myBgPresets.length > 0 && (
          <div className="mb-3" data-testid="tpl-bg-my-presets">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">{t("rbTplBgMyPresetsTitle")}</p>
            <div className="grid grid-cols-4 gap-2">
              {myBgPresets.map((p) => {
                const active = bg?.image === p.image;
                return (
                  <div
                    key={p.id}
                    className="relative group"
                    data-testid={`tpl-bg-mypreset-${p.id}`}
                  >
                    <button
                      onClick={() => applyPreset(p)}
                      title={p.name}
                      className={`w-full aspect-[210/297] border bg-white bg-center bg-cover transition-colors ${active ? "border-neutral-900 ring-2 ring-neutral-900/20" : "border-neutral-300 hover:border-neutral-900"}`}
                      style={{ backgroundImage: `url(${p.image})` }}
                    >
                      <span className="absolute inset-x-0 bottom-0 bg-white/85 backdrop-blur-[1px] font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-700 py-1 px-1 text-center border-t border-black/10 truncate">
                        {p.name}
                      </span>
                      {active && (
                        <span className="absolute top-1 right-1 bg-neutral-900 text-white p-0.5" data-testid={`tpl-bg-mypreset-active-${p.id}`}>
                          <Check size={10} strokeWidth={2.5} />
                        </span>
                      )}
                    </button>
                    <button
                      data-testid={`tpl-bg-mypreset-delete-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteMyPreset(p.id); }}
                      title={t("rbTplBgRemove")}
                      className="absolute top-1 left-1 bg-white/90 border border-rose-300 text-rose-700 p-0.5 opacity-0 group-hover:opacity-100 hover:bg-rose-700 hover:text-white transition"
                    >
                      <Trash2 size={10} strokeWidth={1.8} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Preset picker — one click starter backgrounds */}
        {bgPresets.length > 0 && (
          <div className="mb-3" data-testid="tpl-bg-presets">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">{t("rbTplBgPresetsTitle")}</p>
            <div className="grid grid-cols-4 gap-2">
              {bgPresets.map((p) => {
                const active = bg?.image === p.image;
                return (
                  <button
                    key={p.slug}
                    data-testid={`tpl-bg-preset-${p.slug}`}
                    onClick={() => applyPreset(p)}
                    title={t(p.name_key) || p.slug}
                    className={`relative aspect-[210/297] border bg-white bg-center bg-cover transition-colors ${active ? "border-neutral-900 ring-2 ring-neutral-900/20" : "border-neutral-300 hover:border-neutral-900"}`}
                    style={{ backgroundImage: `url(${p.image})` }}
                  >
                    <span className="absolute inset-x-0 bottom-0 bg-white/85 backdrop-blur-[1px] font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-700 py-1 text-center border-t border-black/10">
                      {t(p.name_key) || p.slug}
                    </span>
                    {active && (
                      <span className="absolute top-1 right-1 bg-neutral-900 text-white p-0.5" data-testid={`tpl-bg-preset-active-${p.slug}`}>
                        <Check size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {bg?.image ? (
          <div className="border border-neutral-300 bg-white p-3 flex flex-col sm:flex-row gap-4">
            <div
              data-testid="tpl-bg-preview"
              className="w-full sm:w-40 aspect-[210/297] border border-neutral-300 bg-neutral-50 bg-center bg-cover shrink-0"
              style={{ backgroundImage: `url(${bg.image})` }}
            />
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <label htmlFor="tpl-bg-opacity" className="ed-label mb-1 flex items-center justify-between">
                  <span>{t("rbTplBgOpacity")}</span>
                  <span className="font-mono text-[0.65rem] text-neutral-700" data-testid="tpl-bg-opacity-value">{bgOpacityPct}%</span>
                </label>
                <input
                  id="tpl-bg-opacity"
                  data-testid="tpl-bg-opacity"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={bgOpacityPct}
                  onChange={(e) => setBgOpacity(Number(e.target.value))}
                  className="w-full accent-neutral-900"
                />
                <p className="text-[0.6rem] font-mono text-neutral-500 mt-1">{t("rbTplBgOpacityHint")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="tpl-bg-replace"
                  onClick={() => bgInputRef.current?.click()}
                  className="ed-btn ed-btn-outline"
                >
                  <ImageIcon size={12} strokeWidth={1.7} /> {t("rbTplBgReplace")}
                </button>
                {!isCurrentBgSaved() && myBgPresets.length < 10 && (
                  <button
                    data-testid="tpl-bg-save-preset"
                    onClick={saveCurrentBgAsPreset}
                    disabled={savingBg}
                    className="ed-btn ed-btn-outline disabled:opacity-50"
                  >
                    <BookmarkPlus size={12} strokeWidth={1.7} /> {t("rbTplBgSavePreset")}
                  </button>
                )}
                <button
                  data-testid="tpl-bg-remove"
                  onClick={clearBackground}
                  className="ed-btn ed-btn-outline text-rose-700 border-rose-300 hover:border-rose-700"
                >
                  <Trash2 size={12} strokeWidth={1.7} /> {t("rbTplBgRemove")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            data-testid="tpl-bg-upload"
            onClick={() => bgInputRef.current?.click()}
            className="ed-card w-full p-6 border-dashed flex flex-col items-center gap-2 hover:border-neutral-900 transition-colors"
          >
            <ImageIcon size={20} strokeWidth={1.5} className="text-neutral-500" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-700">{t("rbTplBgUpload")}</span>
            <span className="text-[0.6rem] font-mono text-neutral-500">{t("rbTplBgFormats")}</span>
          </button>
        )}
      </div>

      {/* Section placement — drag & drop over three zones */}
      <div className="mt-6">
        <p className="ed-label mb-1">{t("rbTplSections")}</p>
        <p className="text-[0.65rem] font-mono text-neutral-500 mb-3">
          {design.layout === "single-column" ? t("rbTplSectionsHintSingle") : t("rbTplSectionsHint")}
        </p>
        <p className="text-[0.65rem] font-mono text-neutral-500 mb-3">{t("tplSecHint")}</p>
        <div className="space-y-3">
          {renderZone("header", "min-h-[4.5rem]")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderZone("side", "min-h-[9rem]")}
            {renderZone("main", "min-h-[9rem]")}
          </div>
        </div>

        {/* Per-section style panel */}
        {styleKey && (
          <div className="mt-4 border border-neutral-900 bg-white p-4" data-testid="tpl-secstyle-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="ed-label flex items-center gap-2">
                <Paintbrush size={12} strokeWidth={1.7} /> {t("tplSecStyle")} — {t(SECTION_LABELS[styleKey])}
              </p>
              <button
                data-testid="tpl-secstyle-close"
                onClick={() => setStyleKey(null)}
                className="p-1.5 border border-neutral-300 hover:border-neutral-900 transition-colors"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="ed-label mb-1">{t("tplSecBorder")}</p>
                <button
                  data-testid="tpl-secstyle-border"
                  onClick={() => setSecStyle(styleKey, { border: !secStyle(styleKey).border })}
                  className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${secStyle(styleKey).border ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
                >
                  <span className={`w-3.5 h-3.5 border flex items-center justify-center ${secStyle(styleKey).border ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
                    {secStyle(styleKey).border && <Check size={10} strokeWidth={3} />}
                  </span>
                  {secStyle(styleKey).border ? t("tplSecBorderOn") : t("tplSecNone")}
                </button>
              </div>
              <ColorField
                label={t("tplSecFill")}
                value={secStyle(styleKey).fill || ""}
                onChange={(v) => setSecStyle(styleKey, { fill: v })}
                testId="tpl-secstyle-fill"
              />
              <ColorField
                label={t("tplSecTextColor")}
                value={secStyle(styleKey).text_color || ""}
                onChange={(v) => setSecStyle(styleKey, { text_color: v })}
                testId="tpl-secstyle-textcolor"
              />
              <ColorField
                label={t("tplSecTitleColor")}
                value={secStyle(styleKey).title_color || ""}
                onChange={(v) => setSecStyle(styleKey, { title_color: v })}
                testId="tpl-secstyle-titlecolor"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
